// src/components/Chat/Message/Chart/PieChartComponent.tsx
import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as TooltipsChart,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Typography } from "@mui/material";

const PieChartComponent: React.FC<{
  data: Array<{ label: string; [key: string]: number | string }>;
  name: string;
  theme: any;
}> = ({ data, name, theme }) => {
  const valueKey = Object.keys(data[0]).find(
    (key) => key !== "label"
  ) as keyof (typeof data)[0];

  if (!valueKey) {
    console.error(
      "PieChartComponent: No value key found in the provided data."
    );
    return null;
  }

  // Calculate the total value for the data
  const totalValue = data.reduce(
    (sum, entry) => sum + (entry[valueKey] as number),
    0
  );

  // Calculate the percentage for each data item
  const dataWithPercentages = data.map((entry) => ({
    ...entry,
    percentage: (((entry[valueKey] as number) / totalValue) * 100).toFixed(2),
  }));

  const getColor = (index: number) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.warning.main,
    ];
    return colors[index % colors.length];
  };

  return (
    <>
      <Typography sx={{ mb: 2, width: "100%", textAlign: "center" }}>
        {name}
      </Typography>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <TooltipsChart
            contentStyle={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            }}
            itemStyle={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
              color: theme.palette.text.primary,
            }}
            labelStyle={{
              color: theme.palette.text.primary,
            }}
            cursor={{ fill: theme.palette.action.hover }}
          />
          <Legend
            wrapperStyle={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
            }}
          />
          <Pie
            data={dataWithPercentages}
            dataKey={valueKey}
            nameKey="label"
            outerRadius={80}
            innerRadius={50}
            label={({ index }) =>
              `${dataWithPercentages[index].label}: ${dataWithPercentages[index].percentage}%`
            }
          >
            {dataWithPercentages.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                stroke={getColor(index)}
                fill={getColor(index)}
                fillOpacity={1}
                fontFamily={theme.typography.fontFamily}
                fontSize={theme.typography.body2.fontSize}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </>
  );
};

export default PieChartComponent;
